import React, { useState } from "react";
import { Box, Button, Menu, MenuItem, Tooltip, Typography, useTheme } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AccessControl, { FEATURE_IDS } from "../../../../../components/AccessControl";
import ScopeActionButton from "../../../../../components/ScopeActionButton/ScopeActionButton";


const BulkActionBar = ({
  count,
  inScopeEligibleCount = 0, 
  showInScope,
  onExport,
  onSendReminder, 
  onMarkOutOfScope, // () => void
  onMarkInScope, // () => void
  onClear,
}) => {
  const theme = useTheme();
  const [reminderAnchor, setReminderAnchor] = useState(null);

  const hasSelection = count > 0;
  const scopeActionDisabled = showInScope
    ? !hasSelection
    : !hasSelection || inScopeEligibleCount === 0;

  // Reason shown in a tooltip when the scope action is disabled.
  let scopeDisabledReason = "";
  if (scopeActionDisabled) {
    if (!hasSelection) {
      scopeDisabledReason = "Select employees first";
    } else if (!showInScope && inScopeEligibleCount === 0) {
      scopeDisabledReason =
        "All selected employees have a termination date. Clear it first to mark them in scope.";
    }
  }

  const primaryBtnSx = {
    bgcolor: theme.customColors.button.primary,
    color: theme.customColors.button.text,
    fontWeight: 500,
    borderRadius: "8px",
    textTransform: "none",
    fontSize: "14px",
    px: 2,
    py: 1,
    "&:hover": { bgcolor: theme.palette._blue?.dark ?? theme.customColors.button.primary },
    "&:disabled": {
      bgcolor: theme.customColors.background.light,
      color: theme.customColors.text.disabled,
    },
  };

  const outlinedBtnSx = {
    fontWeight: 500,
    borderRadius: "8px",
    textTransform: "none",
    fontSize: "14px",
    px: 2,
    py: 1,
    borderColor: theme.customColors.border.main,
    color: theme.customColors.text.strong,
    "&:hover": {
      borderColor: theme.customColors.border.strong,
      backgroundColor: theme.customColors.background.hover,
    },
    "&:disabled": {
      borderColor: theme.customColors.border.disabled,
      color: theme.customColors.text.disabled,
    },
  };

  const menuPaperProps = {
    sx: {
      mt: 1,
      minWidth: 220,
      boxShadow:
        "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)",
      borderRadius: "8px",
      border: "1px solid #E5E7EB",
    },
  };

  const renderMenuItem = (title, subtitle, onClick) => (
    <MenuItem onClick={onClick} sx={{ py: 1.5, px: 2, "&:hover": { backgroundColor: "#F3F4F6" } }}>
      <Box>
        <Typography variant="body2" fontWeight={600} color={theme.customColors.text.strong}>
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            color={theme.customColors.text.secondary}
            sx={{ display: "block", mt: 0.25 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </MenuItem>
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <AccessControl featureId={FEATURE_IDS.PEOPLE_STAFF_CHECKS_EXCEL_EXPORT}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
          onClick={onExport}
          disabled={!hasSelection}
          sx={outlinedBtnSx}
        >
          {hasSelection ? `Export (${count})` : "Export"}
        </Button>
      </AccessControl>

      {showInScope && (
        <AccessControl featureId={FEATURE_IDS.PEOPLE_STAFF_SEND_REMINDER}>
          <Button
            variant="contained"
            startIcon={<NotificationsIcon sx={{ fontSize: 16 }} />}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => setReminderAnchor(e.currentTarget)}
            disabled={!hasSelection}
            sx={primaryBtnSx}
          >
            {hasSelection ? `Send Reminder (${count})` : "Send Reminder"}
          </Button>
        </AccessControl>
      )}

      <Tooltip title={scopeDisabledReason} arrow disableHoverListener={!scopeDisabledReason}>
        <span>
          <ScopeActionButton
            mode={showInScope ? "out" : "in"}
            onClick={showInScope ? onMarkOutOfScope : onMarkInScope}
            disabled={scopeActionDisabled}
          />
        </span>
      </Tooltip>

      {hasSelection && (
        <Button variant="outlined" onClick={onClear} sx={outlinedBtnSx}>
          Clear
        </Button>
      )}

      <Menu
        anchorEl={reminderAnchor}
        open={Boolean(reminderAnchor)}
        onClose={() => setReminderAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={menuPaperProps}
      >
        {renderMenuItem("Policy Reminder", "For pending policy acknowledgements", () => {
          setReminderAnchor(null);
          onSendReminder("policy");
        })}
        {renderMenuItem("Training Reminder", "For pending training completion", () => {
          setReminderAnchor(null);
          onSendReminder("training");
        })}
      </Menu>
    </Box>
  );
};

export default BulkActionBar;
